'use client';

import { useBrand } from '~/components/brand-provider';
import { BalleeLogoHeader } from './brand/ballee-logo/ballee-logo-header';
import { BalleeLogoIcon } from './brand/ballee-logo/ballee-logo-icon';
import { COLOR_SCHEMES, type ColorScheme } from './brand/flow-logo/constants';
import { FlowLogoHeader } from './brand/flow-logo/flow-logo-header';
import { FlowLogoIcon } from './brand/flow-logo/flow-logo-icon';

interface AppLogoBaseProps {
  href?: string | null;
  /** Preset color scheme: auto | white | translucent | brand */
  colorScheme?: ColorScheme;
  /** Raw color override (takes precedence over colorScheme) */
  color?: string;
  className?: string;
  'aria-label'?: string;
}

interface AppLogoIconProps extends AppLogoBaseProps {
  variant?: 'icon';
  size?: 16 | 32 | 48 | 64;
}

interface AppLogoLockupProps extends AppLogoBaseProps {
  variant: 'lockup';
  size?: 'sm' | 'md' | 'lg';
  animateOnHover?: boolean;
  showText?: boolean;
}

type AppLogoProps = AppLogoIconProps | AppLogoLockupProps;

export function AppLogo(props: AppLogoProps) {
  const brand = useBrand();

  const {
    variant = 'icon',
    href = '/',
    color,
    colorScheme = 'auto',
    className,
    'aria-label': ariaLabel,
  } = props;

  const resolvedColor = color ?? COLOR_SCHEMES[colorScheme];

  if (variant === 'lockup') {
    const {
      size = 'sm',
      animateOnHover = true,
      showText = true,
    } = props as AppLogoLockupProps;

    if (brand === 'ballee') {
      return (
        <BalleeLogoHeader
          href={href}
          className={className}
          size={size}
          showText={showText}
          aria-label={ariaLabel}
        />
      );
    }

    return (
      <FlowLogoHeader
        href={href}
        color={resolvedColor}
        className={className}
        size={size}
        animateOnHover={animateOnHover}
        showText={showText}
        aria-label={ariaLabel}
      />
    );
  }

  const { size = 32 } = props as AppLogoIconProps;

  if (brand === 'ballee') {
    return (
      <BalleeLogoIcon
        href={href}
        className={className}
        size={size}
        aria-label={ariaLabel}
      />
    );
  }

  return (
    <FlowLogoIcon
      href={href}
      color={resolvedColor}
      className={className}
      size={size}
      aria-label={ariaLabel}
    />
  );
}
